// InfoCard.jsx
import React from "react";
import { ColorRing } from "react-loader-spinner";
import { Users, MessagesSquare, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function InfoCard({
    users = 0,
    chats = 0,
    todayAvgTokens = 0,
    botInfoLoading,
}) {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = (i18n.language === "en");

    const items = [
        {
            label: t("infoCard.customers"),
            count: users,
            icon: Users,
            tone: "teal",
        },
        {
            label: t("infoCard.chats"),
            count: chats,
            icon: MessagesSquare,
            tone: "gold",
        },
        {
            label: t("infoCard.dailyAvgUsage"),
            count: todayAvgTokens,
            icon: Activity,
            tone: "teal",
        },
    ];

    const Loader = () => (
        <ColorRing
            visible={true}
            height="30"
            width="30"
            ariaLabel="loading"
            colors={["#0F766E", "#14B8A6", "#5EEAD4", "#FCD34D", "#FBBF24"]}
        />
    );

    const IconBox = ({ icon: Icon, tone }) => {
        const toneClass =
            tone === "gold"
                ? "bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_6px_20px_-6px_rgba(251,191,36,0.8)]"
                : "bg-gradient-to-br from-teal-500 to-teal-700 shadow-[0_6px_20px_-6px_rgba(20,184,166,0.8)]";
        return (
            <div
                className={`
                    relative shrink-0 w-12 h-12 flex items-center justify-center
                    rounded-xl text-white ${toneClass}
                    transition-transform duration-500 ease-out
                    group-hover/item:scale-110 group-hover/item:rotate-3
                `}
            >
                <Icon className="w-5 h-5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]" />
                {/* درخشش کوچک گوشه */}
                <span className="pointer-events-none absolute -top-1 -right-1 h-3 w-3 rounded-full bg-white/40 blur-md" />
            </div>
        );
    };

    return (
        <div className="col-span-full">
            <div
                className="
                    relative overflow-hidden
                    grid grid-cols-1 lg:grid-cols-3
                    rounded-2xl
                    bg-gradient-to-br from-white via-teal-50/60 to-amber-50/40
                    dark:!bg-gradient-to-br dark:!from-slate-900 dark:!via-[#0B2E2F] dark:!to-slate-900
                    backdrop-blur-xl
                    border border-teal-500/15 dark:!border-teal-400/15
                    shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
                    dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
                    transition-colors duration-500
                "
            >
                {/* خط تزئینی طلایی بالا */}
                <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

                {/* گلوی طلایی گوشه بالا-راست */}
                <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 blur-3xl dark:!bg-amber-400/10" />

                {/* گلوی teal گوشه پایین-چپ */}
                <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 blur-3xl dark:!bg-teal-500/10" />

                {items.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                        <div
                            key={idx}
                            className={`
                                group/item relative
                                flex justify-between items-center gap-3
                                px-5 sm:px-6 py-5
                                transition-colors duration-300
                                hover:bg-white/40 dark:hover:!bg-white/5

                                ${idx !== items.length - 1
                                    ? "border-b border-teal-500/10 dark:!border-teal-400/10 lg:border-b-0 lg:rtl:border-l lg:ltr:border-r"
                                    : ""
                                }
                            `}
                        >
                            {/* جداکننده عمودی گرادیانت در دسکتاپ */}
                            {idx !== items.length - 1 && (
                                <span
                                    className="
                                        pointer-events-none hidden lg:block
                                        absolute top-3 bottom-3 rtl:left-0 ltr:right-0 w-px
                                        bg-gradient-to-b from-transparent via-teal-400/40 to-transparent
                                    "
                                    aria-hidden="true"
                                />
                            )}

                            {/* Label */}
                            <div className="min-w-0 flex-1">
                                <p className="text-slate-600 dark:!text-slate-300 text-sm sm:text-[15px] font-medium leading-relaxed">
                                    {item?.label ?? ""}
                                </p>
                                <div className="mt-1.5 h-[2px] w-8 rounded-full bg-gradient-to-l from-amber-300/70 to-transparent" />
                            </div>

                            {/* Value + Icon */}
                            <div className="flex items-center gap-3 shrink-0">
                                <div className="min-w-[60px] text-left rtl:text-right">
                                    {botInfoLoading ? (
                                        <Loader />
                                    ) : (
                                        <p className={`text-2xl sm:text-[26px] font-extrabold tabular-nums bg-gradient-to-l from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent drop-shadow-[0_1px_6px_rgba(251,191,36,0.35)] ${isEnglish && 'font-sans'}`}>
                                            {item?.count?.toLocaleString?.("en-US") ?? 0}
                                        </p>
                                    )}
                                </div>
                                <IconBox icon={Icon} tone={item.tone} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}